import { FC } from 'react';
import NumberFormat from 'react-number-format';

import { numberFormat } from 'constants/formats';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import LoadingButton from 'modules/_shared/components/LoadingButton';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import { HistoryMovementsViewProvider } from 'modules/_shared/components/HistoryMovementsView/context/HistoryMovementsViewContext';
import HistoryMovementsView from 'modules/_shared/components/HistoryMovementsView';

import { addPartnerOption } from 'constants/addPartnerOption';
import operationTypes from 'constants/operationTypes';
import { useCapitalIncreaseModal } from '../context/CapitalIncreaseModalContext';
import AddPartnerEmbeddedModal from '../../AddPartnerEmbedded';

const CapitalIncreaseModalMovementsView: FC = () => {
  const { t } = useTranslate();

  const { data, actions } = useCapitalIncreaseModal();

  const validateRangeButtonDisabled =
    data.sharesToAdd <= 0 ||
    !data.selectedHoldingClass ||
    data.sharesToAdd > data.remainingShares ||
    !data.isValidateRangeEnabled;

  const handleSearchPartnerByTerm = async (searchTerm: string) => {
    const options = await actions.handleSearchPartnerByTerm(searchTerm, true);
    return options;
  };
  return (
    <>
      <label className="form-label mb-4">
        <span className="pb-2">{t('TotalNewParticipationsPen')}:</span>
        <span className="badge-primary rounded px-2 py-1 ml-2">
          {formatNumber(data.remainingShares >= 0 ? data.remainingShares : 0)}
        </span>
      </label>

      {!data.showAddPartnerSection ? (
        <div className="form-group">
          <label className="form-label" htmlFor="partner-select-input">
            {t('SelectPartner')} <MandatoryField />
          </label>
          <div className="form-control-wrap">
            <CustomAsyncSelect
              id="partner-select-input"
              onChange={actions.handleChangePartner}
              onFetchData={handleSearchPartnerByTerm}
              placeholder={t('SearchPartner')}
              minimumInputLength={3}
              noDataLabelKey="NoPartnersHaveBeenFound"
              loadingLabelKey="LoadingPartners"
              errorLabelKey="ErrorLoadingPartners"
              defaultOptions={data.partnerOptions}
              addOption={{
                ...addPartnerOption,
                label: `+ ${t(addPartnerOption.label)}`,
              }}
            />
          </div>
        </div>
      ) : (
        <AddPartnerEmbeddedModal
          onCreate={actions.handleAddPartner}
          onClose={actions.handleCloseAddPartnerSection}
          date={data.date}
        />
      )}

      <div className="form-group">
        <label className="form-label" htmlFor="shares-to-add">
          {t('NºNewParticipations')} <MandatoryField />
        </label>

        <div className="input-group">
          <NumberFormat
            id="shares-to-add"
            className="form-control form-control-lg"
            value={data.sharesToAdd}
            onChange={actions.handleChangeSharesToAdd}
            thousandSeparator={numberFormat.thousandSeparator}
            decimalSeparator={numberFormat.decimalSeparator}
            allowNegative={false}
            placeholder={t('NumberOfNewParticipations')}
            decimalScale={0}
            required
          />

          <LoadingButton
            isLoading={data.isLoadingRange}
            onClick={actions.handleValidateRange}
            text={t('Validate')}
            icon="ni-check"
            className="btn btn-outline-primary btn-dim border-radius-0"
            disabled={validateRangeButtonDisabled}
          />
        </div>
      </div>

      <div className="custom-control custom-checkbox">
        <input
          type="checkbox"
          id="has-premium-checkbox"
          className="custom-control-input"
          onChange={actions.handleChangeHasPremium}
        />
        <label
          className="custom-control-label mb-2"
          htmlFor="has-premium-checkbox"
        >
          {t('PremiumAmountForParticipati')}
        </label>
      </div>

      {data.hasPremium && (
        <div className="form-group">
          <label className="form-label" htmlFor="premium">
            {t('Premium')} <MandatoryField />
          </label>

          <div className="form-control-wrap">
            <NumberFormat
              id="premium"
              className="form-control form-control-lg"
              value={data.premium}
              onChange={actions.handleChangePremium}
              thousandSeparator={numberFormat.thousandSeparator}
              decimalSeparator={numberFormat.decimalSeparator}
              allowNegative={false}
              decimalScale={10}
              required
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="form-label" htmlFor="class-select-input">
          {t('ClassOfParticipations')} <MandatoryField />
        </label>
        <div className="form-control-wrap">
          <CustomAsyncSelect
            id="class-select-input"
            value={data.selectedHoldingClassOption}
            onChange={actions.handleChangeHoldingClass}
            onFetchData={actions.handleFetchHoldingClassByTerm}
            placeholder={t('SearchClass')}
            minimumInputLength={1}
            noDataLabelKey="NoClassesHaveBeenFound"
            loadingLabelKey="LoadingClasses"
            errorLabelKey="ErrorLoadingClasses"
            defaultOptions={data.holdingClassOptions}
          />
        </div>
        {data.selectedHoldingClass && (
          <span className="sub-text text-primary mt-1">
            {t('From')} {data.selectedHoldingClassOption?.numerationOptionName}
          </span>
        )}
      </div>

      <div className="form-group row">
        <div className="col-6">
          <label className="form-label" htmlFor="range-from">
            {t('OfTheNumber')}
          </label>
          {data.isLoadingRange ? (
            <div className="form-control placeholder-glow h-40px d-flex justify-content-center align-items-center">
              <span className="placeholder col-4" />
            </div>
          ) : (
            <input
              id="range-from"
              type="text"
              className="form-control form-control-lg h-40px text-center"
              value={formatNumber(data.availableRange.from)}
              disabled
              readOnly
            />
          )}
        </div>

        <div className="col-6">
          <label className="form-label" htmlFor="range-to">
            {t('ToTheNumber')}
          </label>
          {data.isLoadingRange ? (
            <div className="form-control placeholder-glow h-40px d-flex justify-content-center align-items-center">
              <span className="placeholder col-4" />
            </div>
          ) : (
            <input
              id="range-to"
              type="text"
              className="form-control form-control-lg h-40px text-center"
              value={formatNumber(data.availableRange.to)}
              disabled
              readOnly
            />
          )}
        </div>
      </div>

      <div className="custom-control custom-checkbox">
        <input
          type="checkbox"
          id="useFirstAvailableRange-checkbox"
          className="custom-control-input"
          onChange={actions.handleChangeUseFirstAvailableRange}
        />
        <label
          className="custom-control-label mb-2"
          htmlFor="useFirstAvailableRange-checkbox"
        >
          {t('UseFirstAvailableClassRange')}
        </label>
      </div>

      <div className="mt-4">
        <HistoryMovementsViewProvider
          historyMovements={data.historyMovements}
          operationType={operationTypes.CAPITAL_INCREASE}
        >
          <HistoryMovementsView />
        </HistoryMovementsViewProvider>
      </div>
    </>
  );
};

export default CapitalIncreaseModalMovementsView;
